import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Dropdown, Button, Icon } from 'semantic-ui-react';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import {
    detectLocationByGeo,
    setUserRegion,
    initGeoLocation,
    RUSSIA_REGIONS_LIST,
    geoSelectors,
} from '../../features/geolocation';
import './RegionSelector.css';

const RegionSelector = ({ className = '' }) => {
    const dispatch = useDispatch();

    const region = useSelector(geoSelectors.selectRegion);
    const regionCode = useSelector(geoSelectors.selectRegionCode);
    const isFetching = useSelector(geoSelectors.selectIsGeoFetching);
    const isLoaded = useSelector(geoSelectors.selectIsGeoLoaded);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRegion, setSelectedRegion] = useState('');

    useEffect(() => {
        dispatch(initGeoLocation());
    }, [dispatch]);

    useEffect(() => {
        if (isModalOpen && regionCode) {
            setSelectedRegion(regionCode);
        }
    }, [isModalOpen, regionCode]);

    const handleOpenModal = useCallback(() => {
        setSelectedRegion(regionCode || '');
        setIsModalOpen(true);
        dispatch(detectLocationByGeo());
    }, [dispatch, regionCode]);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    const handleRegionChange = useCallback((e, { value }) => {
        setSelectedRegion(value);
    }, []);

    const handleConfirmRegion = useCallback(() => {
        const selected = RUSSIA_REGIONS_LIST.find(r => r.value === selectedRegion);
        if (selected) {
            dispatch(setUserRegion(selected.text, selected.value));
        }
        setIsModalOpen(false);
    }, [dispatch, selectedRegion]);

    const handleRefresh = useCallback(() => {
        dispatch(detectLocationByGeo(true));
    }, [dispatch]);

    const displayRegion = region || 'Выберите регион';

    return (
        <>
            <div
                className={`region-selector ${className}`}
                onClick={handleOpenModal}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => e.key === 'Enter' && handleOpenModal()}
            >
                <Icon name="map marker alternate" className="region-selector__icon" />
                {isFetching && !isLoaded ? (
                    <span className="region-selector__text region-selector__text--loading">
                        <Spin indicator={<LoadingOutlined spin style={{ fontSize: 14 }} />} size="small" /> Определяем...
                    </span>
                ) : (
                    <span className="region-selector__text">{displayRegion}</span>
                )}
            </div>

            <Modal
                open={isModalOpen}
                onClose={handleCloseModal}
                size="small"
                className="region-select-modal"
            >
                <Modal.Header>
                    Выберите ваш регион
                </Modal.Header>
                <Modal.Content>
                    <div className="region-modal-content">
                        <p className="region-modal-hint">
                            Выбор региона влияет на наличие товаров и условия доставки
                        </p>
                        <Dropdown
                            placeholder="Выберите регион"
                            fluid
                            search
                            selection
                            options={RUSSIA_REGIONS_LIST}
                            value={selectedRegion}
                            onChange={handleRegionChange}
                            className="region-dropdown"
                            noResultsMessage="Регион не найден"
                        />
                        <button
                            type="button"
                            className="region-auto-detect"
                            onClick={handleRefresh}
                            disabled={isFetching}
                        >
                            <Icon name="map marker alternate" />
                            {isFetching ? 'Определяем...' : 'Определить автоматически'}
                        </button>
                    </div>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={handleCloseModal}>
                        Отмена
                    </Button>
                    <Button
                        primary
                        onClick={handleConfirmRegion}
                        disabled={!selectedRegion}
                    >
                        Выбрать
                    </Button>
                </Modal.Actions>
            </Modal>
        </>
    );
};

export default RegionSelector;